/**
 * Other racers ("ghosts"): their reported positions, buffered by the sender's race time and
 * replayed a little in the past, interpolated between the two samples around that moment.
 *
 * - Render time = race time now − the playout delay (GhostStore.delayMs), which hides network
 *   jitter. The delay is a jitter buffer: it follows how late each racer's samples arrive
 *   (arrival on the receiver's race clock − the sample's `tm` on the sender's: the errors of the
 *   two clocks' sync with the server add up in it rather than cancel, but the ghost is replayed
 *   on the same two clocks, so the delay has to cover them too): the 90th percentile over the
 *   last LATENESS_WINDOW_MS, plus one send interval and a margin, kept within
 *   MIN_DELAY_MS..MAX_DELAY_MS. One delay for everybody, the largest any racer still sending
 *   needs, keeps the ghosts where they are relative to each other. It eases toward that target:
 *   the ghosts' time runs at most 10 % faster or slower than real time, never jumps. A page that
 *   was frozen (in the background, a long pause of the main thread) handles the batches held
 *   meanwhile all at once on waking: late because of the page, not the network, they do not
 *   count (FROZEN_MS).
 * - No smearing across jumps in a run: between samples of different levels or attempts, or
 *   when the racer died or came back to life, the ghost snaps from one to the other; across a
 *   spider's teleport its height snaps (x runs on), and it is not extrapolated from one.
 * - Past the newest sample the ghost moves on with the velocity of the last two samples of its
 *   run: x for at most MAX_EXTRAPOLATION_X_MS (nothing but the run's speed moves x, so late
 *   data costs little accuracy), y for MAX_EXTRAPOLATION_Y_MS, after which y holds. Then it
 *   waits. A dead racer, or one whose newest sample starts a new level or attempt, waits at once.
 * - A racer without updates for STALE_AFTER_MS of render time (offline, finished, gave up) is
 *   stale.
 */
import {
  type Gravity,
  type Mode,
  type PlayerId,
  type PlayerNetState,
  STATE_SEND_HZ,
} from '@cube-rush/shared';
import { ballRollAngle, lerpAngle } from './pose';

/** The playout delay until enough samples tell how late they arrive. */
export const INITIAL_DELAY_MS = 150;
export const MIN_DELAY_MS = 90;
export const MAX_DELAY_MS = 450;
export const MAX_EXTRAPOLATION_X_MS = 400;
export const MAX_EXTRAPOLATION_Y_MS = 100;
export const STALE_AFTER_MS = 2000;
/** Samples older than this (relative to the newest) are dropped. */
const HISTORY_MS = 3000;
const MAX_SAMPLES = 64;
/** How late a racer's samples arrive is judged over this much time... */
export const LATENESS_WINDOW_MS = 3000;
/** ...at most this many samples (15 a second)... */
const MAX_LATENESS_SAMPLES = 45;
/** ...and only once there are this many. */
const MIN_LATENESS_SAMPLES = 10;
/** The delay covers the lateness percentile plus a send interval and this margin. */
const DELAY_MARGIN_MS = 10;
const SEND_INTERVAL_MS = 1000 / STATE_SEND_HZ;
/** Per frame the delay moves by at most this share of the frame's length: up... */
const DELAY_GROWTH = 0.1;
/** ...and down (slower: a delay that is too short shows, one that is too long does not). */
const DELAY_SHRINK = 0.03;
/**
 * A page that handled nothing (no frame, no batch) for longer than this was frozen: in the
 * background (iOS) or by a long pause of the main thread. On waking it handles the batches held
 * meanwhile all at once, stamped late by the page, not by the network...
 */
const FROZEN_MS = 250;
/** ...so the batches handled this soon after its first sign of life are shown but not timed. */
const WAKING_MS = 100;

/** A ghost at one moment (reused between frames). */
export interface GhostPose {
  lvl: number;
  att: number;
  x: number;
  y: number;
  rot: number;
  mode: Mode;
  grav: Gravity;
  alive: boolean;
  fx: number;
  /** Race time of the newest sample, to tell how fresh the data is. */
  updatedAt: number;
}

export function createGhostPose(): GhostPose {
  return {
    lvl: 0,
    att: 1,
    x: 0,
    y: 0,
    rot: 0,
    mode: 'cube',
    grav: 1,
    alive: true,
    fx: 0,
    updatedAt: Number.NEGATIVE_INFINITY,
  };
}

/** Same run segment: interpolating between the two samples makes sense. */
function continuous(a: PlayerNetState, b: PlayerNetState): boolean {
  return a.lvl === b.lvl && a.att === b.att && a.alive === b.alive;
}

/**
 * Smallest jump in height (blocks) between two samples of a spider whose gravity flipped that is
 * a teleport: no fall between two samples (1/15 s) gets that far.
 */
export const TELEPORT_DY = 1.9;

/** A spider teleported between the two samples: its height jumps rather than glides. */
export function teleported(a: Pick<PlayerNetState, 'mode' | 'grav' | 'y'>, b: typeof a): boolean {
  return (
    a.mode === 'spider' &&
    b.mode === 'spider' &&
    a.grav !== b.grav &&
    Math.abs(b.y - a.y) >= TELEPORT_DY
  );
}

function copy(s: PlayerNetState, out: GhostPose): void {
  out.lvl = s.lvl;
  out.att = s.att;
  out.x = s.x;
  out.y = s.y;
  out.rot = s.rot;
  out.mode = s.mode;
  out.grav = s.grav;
  out.alive = s.alive;
  out.fx = s.fx;
}

/** The samples of one racer, ordered by their race time. */
export class GhostTrack {
  private readonly samples: PlayerNetState[] = [];

  get size(): number {
    return this.samples.length;
  }

  /** Race time of the newest sample (−∞ without samples). */
  get newestTm(): number {
    return this.newest?.tm ?? Number.NEGATIVE_INFINITY;
  }

  get newest(): PlayerNetState | undefined {
    // Not samples.at(-1): Array.prototype.at is missing before iOS 15.4.
    return this.samples[this.samples.length - 1];
  }

  /** Adds a sample; late ones are sorted in, a repeated time replaces the old sample. */
  push(state: PlayerNetState): void {
    const samples = this.samples;
    let i = samples.length;
    while (i > 0 && samples[i - 1]!.tm > state.tm) i--;
    if (i > 0 && samples[i - 1]!.tm === state.tm) samples[i - 1] = state;
    else samples.splice(i, 0, state);
    const newest = samples[samples.length - 1]!.tm;
    let drop = 0;
    // Keep one sample older than the history window so interpolation still has a start.
    while (drop < samples.length - 2 && samples[drop + 1]!.tm < newest - HISTORY_MS) drop++;
    drop = Math.max(drop, samples.length - MAX_SAMPLES);
    if (drop > 0) samples.splice(0, drop);
  }

  /** Writes the ghost at race time `time` into `out`. Returns false without samples. */
  sample(time: number, out: GhostPose): boolean {
    const samples = this.samples;
    const n = samples.length;
    if (n === 0) return false;
    const first = samples[0]!;
    const last = samples[n - 1]!;
    out.updatedAt = last.tm;
    if (time <= first.tm) {
      copy(first, out);
      this.finishRotation(out);
      return true;
    }
    if (time >= last.tm) {
      copy(last, out);
      const before = samples[n - 2];
      if (before && continuous(before, last) && last.alive && last.tm > before.tm) {
        const ahead = time - last.tm;
        const span = last.tm - before.tm;
        out.x = last.x + ((last.x - before.x) / span) * Math.min(ahead, MAX_EXTRAPOLATION_X_MS);
        if (!teleported(before, last)) {
          out.y = last.y + ((last.y - before.y) / span) * Math.min(ahead, MAX_EXTRAPOLATION_Y_MS);
        }
      }
      this.finishRotation(out);
      return true;
    }
    // Binary search for the last sample at or before `time`.
    let lo = 0;
    let hi = n - 1;
    while (hi - lo > 1) {
      const mid = (lo + hi) >>> 1;
      if (samples[mid]!.tm <= time) lo = mid;
      else hi = mid;
    }
    const a = samples[lo]!;
    const b = samples[hi]!;
    if (!continuous(a, b)) {
      // Snap: hold the old segment until the new one begins.
      copy(a, out);
      this.finishRotation(out);
      return true;
    }
    const t = (time - a.tm) / (b.tm - a.tm);
    const nearest = t < 0.5 ? a : b;
    copy(nearest, out);
    out.x = a.x + (b.x - a.x) * t;
    if (!teleported(a, b)) out.y = a.y + (b.y - a.y) * t;
    out.rot = a.mode === b.mode ? lerpAngle(a.rot, b.rot, t) : nearest.rot;
    this.finishRotation(out);
    return true;
  }

  /** The ball's spin is too fast for 15 Hz samples; it follows from its position instead. */
  private finishRotation(out: GhostPose): void {
    if (out.mode === 'ball') out.rot = ballRollAngle(out.x, out.grav);
  }
}

/** How late one racer's samples arrive, over the last LATENESS_WINDOW_MS. */
class Lateness {
  /** Arrival (receiver's race time) and lateness of each recent sample, oldest first. */
  private readonly arrivals: number[] = [];
  private readonly values: number[] = [];
  /** Arrival of the newest sample. */
  lastArrival = Number.NEGATIVE_INFINITY;
  /** The delay this racer's samples need; null until MIN_LATENESS_SAMPLES are known. */
  target: number | null = null;

  add(arrival: number, lateness: number, scratch: number[]): void {
    const { arrivals, values } = this;
    arrivals.push(arrival);
    values.push(lateness);
    while (arrivals.length > MAX_LATENESS_SAMPLES || arrivals[0]! < arrival - LATENESS_WINDOW_MS) {
      arrivals.shift();
      values.shift();
    }
    this.lastArrival = arrival;
    if (values.length < MIN_LATENESS_SAMPLES) {
      this.target = null;
      return;
    }
    scratch.length = 0;
    for (const value of values) scratch.push(value);
    scratch.sort((a, b) => a - b);
    const p90 = scratch[Math.floor(scratch.length * 0.9)]!;
    this.target = Math.min(
      MAX_DELAY_MS,
      Math.max(MIN_DELAY_MS, p90 + SEND_INTERVAL_MS + DELAY_MARGIN_MS),
    );
  }
}

/** Every remote racer's track, and the playout delay they are shown with. */
export class GhostStore {
  private readonly tracks = new Map<PlayerId, GhostTrack>();
  private readonly lateness = new Map<PlayerId, Lateness>();
  /** Sorting space for the lateness percentile (no allocation per batch). */
  private readonly scratch: number[] = [];
  private delay = INITIAL_DELAY_MS;
  /** Race time the page last handled a frame or a batch (null before the first). */
  private lastAlive: number | null = null;
  /** Race time the page woke up after it was frozen (see FROZEN_MS). */
  private wokeAt = Number.NEGATIVE_INFINITY;

  /** How far in the past the ghosts are shown, ms. */
  get delayMs(): number {
    return this.delay;
  }

  /**
   * Feeds a batch from GameNetBridge.onStates (the local player is never included; the server
   * sends only the newest sample of each racer). `arrival` is the receiver's race time when it
   * arrived (the synchronized server clock minus startAt). Batches a frozen page handles on
   * waking do not count toward the delay (see FROZEN_MS).
   */
  receive(players: ReadonlyArray<{ id: PlayerId; s: PlayerNetState }>, arrival: number): void {
    this.alive(arrival);
    const timed = arrival - this.wokeAt > WAKING_MS;
    for (const { id, s } of players) {
      let track = this.tracks.get(id);
      let lateness = this.lateness.get(id);
      if (!track || !lateness) {
        track = new GhostTrack();
        lateness = new Lateness();
        this.tracks.set(id, track);
        this.lateness.set(id, lateness);
      }
      track.push(s);
      if (timed) lateness.add(arrival, arrival - s.tm, this.scratch);
    }
  }

  /**
   * Eases the delay toward what the samples need, for a frame of `frameMs` at race time
   * `raceTime`: the largest target of the racers heard from within LATENESS_WINDOW_MS (one who
   * stopped sending, e.g. finished, no longer counts). Without any, the delay stays.
   */
  adapt(frameMs: number, raceTime: number): void {
    this.alive(raceTime);
    let target = Number.NEGATIVE_INFINITY;
    for (const lateness of this.lateness.values()) {
      if (lateness.target === null || raceTime - lateness.lastArrival > LATENESS_WINDOW_MS) {
        continue;
      }
      target = Math.max(target, lateness.target);
    }
    if (target === Number.NEGATIVE_INFINITY || frameMs <= 0) return;
    const step = frameMs * (target > this.delay ? DELAY_GROWTH : DELAY_SHRINK);
    this.delay += Math.max(-step, Math.min(step, target - this.delay));
  }

  /** The page handled a frame or a batch at race time `time`. */
  private alive(time: number): void {
    const last = this.lastAlive;
    if (last !== null && time - last > FROZEN_MS) this.wokeAt = time;
    this.lastAlive = last === null ? time : Math.max(last, time);
  }

  track(id: PlayerId): GhostTrack | undefined {
    return this.tracks.get(id);
  }

  /**
   * Writes racer `id` at race time `time` into `out`. Returns false when nothing is known
   * about the racer yet.
   */
  sample(id: PlayerId, time: number, out: GhostPose): boolean {
    return this.tracks.get(id)?.sample(time, out) ?? false;
  }

  /**
   * No update from `id` for STALE_AFTER_MS of race time before `time` (or never). Measured at
   * the time the ghosts are shown: a long delay still has the racer's last moments to show.
   */
  isStale(id: PlayerId, time: number): boolean {
    const newest = this.tracks.get(id)?.newestTm ?? Number.NEGATIVE_INFINITY;
    return time - newest > STALE_AFTER_MS;
  }
}
